import type { ValidationIssues } from '~/models/common'
import {
  buildConnectionRecord,
  type ConnectionInput,
  type ConnectionProvider,
  type ConnectionRecord,
} from '~/models/connection'
import type { IsoTimestamp } from '~/types/dates'
import { toIsoTimestamp } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

/**
 * The one store that holds a secret (D-095 §1).
 *
 * Keyed by provider, so one connection per provider is structural rather than a
 * validation rule — a second Worker token is unrepresentable, not merely
 * rejected.
 *
 * **This repository holds no policy about when a token may be used.** Whether
 * the feature is available depends on connectivity, on the Worker's kill switch
 * and on the meter, none of which this file can see. That belongs in the review
 * service, on the same reasoning that keeps the daily check's correction
 * boundary out of `dailyCheckRepository`.
 */

export async function getConnection(
  db: SolaDatabase,
  provider: ConnectionProvider,
): Promise<Result<ConnectionRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.connections, provider))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Store or replace a token.
 *
 * Replacing rather than versioning is deliberate: there is no history of past
 * credentials, because a rotated token has no legitimate reader and keeping one
 * would mean the store held secrets the user believed they had replaced.
 */
export async function saveConnection(
  db: SolaDatabase,
  input: ConnectionInput,
  now: Date,
): Promise<Result<ConnectionRecord, StorageError | ValidationIssues>> {
  const timestamp: IsoTimestamp = toIsoTimestamp(now)

  const existing = await getConnection(db, input.provider as ConnectionProvider)
  if (!existing.ok) return existing

  const built = buildConnectionRecord(input, timestamp, existing.value)
  if (!built.ok) return err(built.error)

  try {
    await db.put(STORE.connections, built.value)
    notifyWritten(STORE.connections)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Forget a token.
 *
 * D-095 §1: *"Deleting the token is clearing the field in Settings."* It is a
 * credential rather than a health record, so it belongs to **L-013**'s
 * settings-only reset and **not** to `selectiveDeletionService.ts` — which is
 * why this is a plain delete here rather than a new deletion category. A full
 * reset still removes it, because `ALL_STORE_NAMES` is derived from the schema.
 */
export async function clearConnection(
  db: SolaDatabase,
  provider: ConnectionProvider,
): Promise<Result<void, StorageError>> {
  try {
    await db.delete(STORE.connections, provider)
    notifyWritten(STORE.connections)
    return ok(undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Every stored connection.
 *
 * Exists for the backup guard, which must be able to assert that a populated
 * store produces an empty block in the file. Nothing on a screen calls it —
 * a screen asks about one provider, and a list of credentials is not something
 * any surface in this app has a reason to hold.
 */
export async function listConnections(
  db: SolaDatabase,
): Promise<Result<readonly ConnectionRecord[], StorageError>> {
  try {
    return ok(await db.getAll(STORE.connections))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
